import { forwardRef, useState, useMemo } from 'react'
import { Form, Row, Col, PopoverProps } from 'react-bootstrap'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Select from 'components/Select/Select'
import { getStatesByCountryID } from 'apis/vehicleApis'
import { IDropdownData } from 'types/baseTypes'
import { IBuyerProfileData } from 'types/accountTypes'
import {
  StyledPopover,
  PopoverHeader,
  PopoverBody,
  CloseButton,
  FormSection,
  ButtonGroup,
  SaveButton,
  CancelButton
} from './AccountSettingPopover.styled'

export interface IAddLocationFormValues {
  address: string
  address2?: string
  city: string
  countryCode: number | null
  stateCode: number | null
  zipcode: string
}

interface IProps extends Omit<PopoverProps, 'id' | 'onSubmit'> {
  initialValues: IAddLocationFormValues
  onSubmit: (data: IAddLocationFormValues) => Promise<void>
  setShowPopover: (show: boolean) => void
  accountData: IBuyerProfileData | undefined
}
const AddLocationPopover = forwardRef(
  ({ initialValues, onSubmit, setShowPopover, accountData, ...rest }: IProps, ref) => {
    const [dynamicStates, setDynamicStates] = useState<IDropdownData[]>([])
    const [isLoadingStates, setIsLoadingStates] = useState(false)
    const [hasCountryBeenSelected, setHasCountryBeenSelected] = useState(false)
    const [stateError, setStateError] = useState<string>('')

    // Validation schema for the address form
    const validationSchema = Yup.object({
      address: Yup.string().required('Address is required'),
      city: Yup.string().required('City is required'),
      countryCode: Yup.number().nullable().required('Country is required'),
      stateCode: Yup.number().nullable().required('State is required'),
      zipcode: Yup.string()
        .required('Zipcode is required')
        .matches(/^\d{5}$/, 'Zipcode must be 5 digits')
    })

    const stateOptions = useMemo(() => {
      const statesToUse = hasCountryBeenSelected ? dynamicStates : accountData?.StateList || []
      return statesToUse.map((state) => ({ value: state.ID, label: state.Description }))
    }, [dynamicStates, accountData?.StateList, hasCountryBeenSelected])

    const handleCountryChange = async (selectedCountry: any, formikProps: any) => {
      formikProps.setFieldValue('countryCode', selectedCountry?.value || null)
      formikProps.setFieldValue('stateCode', null)

      if (!selectedCountry || !selectedCountry.value) {
        setDynamicStates([])
        setHasCountryBeenSelected(false)
        return
      }

      setHasCountryBeenSelected(true)
      setStateError('')
      try {
        setIsLoadingStates(true)
        const states = await getStatesByCountryID(selectedCountry.value)
        setDynamicStates(states)
        if (states.length === 0) {
          setStateError('No states available for the selected country')
        }
      } catch (error) {
        console.error('Error fetching states:', error)
        setDynamicStates([])
        setStateError('Failed to load states. Please try again.')
      } finally {
        setIsLoadingStates(false)
      }
    }

    const handleCancel = (resetForm: () => void) => {
      resetForm()
      setShowPopover(false)
      setHasCountryBeenSelected(false)
      setDynamicStates([])
      setStateError('')
    }

    return (
      <StyledPopover {...rest} ref={ref} id="add-location-popover">
        <PopoverHeader>
          <h3>ADD LOCATION</h3>
          <CloseButton variant="none" onClick={() => setShowPopover(false)}>
            ×
          </CloseButton>
        </PopoverHeader>
        <PopoverBody>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <FormSection>
                  <div>
                    <label className="required-field">Address</label>
                    <Field
                      type="text"
                      placeholder="Enter address"
                      name="address"
                      as={Form.Control}
                      className={`form-control ${props.errors.address && props.touched.address ? 'is-invalid' : ''}`}
                    />
                    <ErrorMessage name="address" component="div" className="error-message" />
                  </div>

                  <div style={{ marginTop: '8px' }}>
                    <label>Address 2</label>
                    <Field
                      type="text"
                      name="address2"
                      placeholder="Enter address 2"
                      as={Form.Control}
                      className="form-control"
                    />
                    <ErrorMessage name="address2" component="div" className="error-message" />
                  </div>

                  <Row style={{ marginTop: '8px' }}>
                    <Col sm={6}>
                      <div>
                        <label className="required-field">City</label>
                        <Field
                          type="text"
                          name="city"
                          placeholder="Enter city"
                          as={Form.Control}
                          className={`form-control ${props.errors.city && props.touched.city ? 'is-invalid' : ''}`}
                        />
                        <ErrorMessage name="city" component="div" className="error-message" />
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div>
                        <label className="required-field">Country</label>
                        <Select
                          name="countryCode"
                          placeholder="Select country"
                          defaultValue={accountData?.defaultCountry || '-1'}
                          options={accountData?.CountryList?.map((x) => ({ value: x.ID, label: x.Description })) || []}
                          onChange={(selectedCountry) => handleCountryChange(selectedCountry, props)}
                        />
                        <ErrorMessage name="countryCode" component="div" className="error-message" />
                      </div>
                    </Col>
                  </Row>

                  <Row style={{ marginTop: '8px' }}>
                    <Col sm={6}>
                      <div>
                        <label className="required-field">State</label>
                        <Select
                          name="stateCode"
                          placeholder={
                            !hasCountryBeenSelected
                              ? 'Please select a country first'
                              : isLoadingStates
                              ? 'Loading states...'
                              : stateOptions.length === 0
                              ? 'No states available'
                              : 'Select state'
                          }
                          options={stateOptions}
                          onChange={(selectedState: { value: number; label: string } | null) => {
                            props.setFieldValue('state', selectedState)
                            props.setFieldValue('stateCode', selectedState?.value || null)
                          }}
                          isDisabled={!hasCountryBeenSelected || isLoadingStates}
                        />
                        <ErrorMessage name="stateCode" component="div" className="error-message" />
                        {stateError && <div className="error-message">{stateError}</div>}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div>
                        <label className="required-field">Zipcode</label>
                        <Field
                          type="text"
                          name="zipcode"
                          placeholder="Enter zipcode"
                          as={Form.Control}
                          className={`form-control ${
                            props.errors.zipcode && props.touched.zipcode ? 'is-invalid' : ''
                          }`}
                          maxLength={5}
                        />
                        <ErrorMessage name="zipcode" component="div" className="error-message" />
                      </div>
                    </Col>
                  </Row>

                  <ButtonGroup className="button-group">
                    <CancelButton variant="light" onClick={() => handleCancel(props.resetForm)}>
                      Cancel
                    </CancelButton>
                    <SaveButton variant="primary" type="submit" disabled={!props.isValid || props.isSubmitting}>
                      Save
                    </SaveButton>
                  </ButtonGroup>
                </FormSection>
              </Form>
            )}
          </Formik>
        </PopoverBody>
      </StyledPopover>
    )
  }
)
export default AddLocationPopover
